// @noflow
/* global fetch:false window */

import reactCookie from 'react-cookies';
import cookie from 'cookie';
import { assign, forEach, throttle, toLower } from 'lodash';
import isReactNative from '../app/isReactNative';

let forceLogout = false;

export class RequestError {
  static Type = { timeout: 'Timeout', httpNonOk: 'HttpNonOk', forceLogout: 'ForceLogout' };

  constructor(type, httpErrorCode, response) {
    this._type = type;
    this._httpErrorCode = httpErrorCode;
    this._response = response;
  }

  get type() {
    return this._type;
  }

  get httpErrorCode() {
    return this._httpErrorCode;
  }

  get response() {
    return this._response;
  }
}

const isClient = typeof document !== 'undefined';

export function paramify(data, excludeUndefined) {
  if (!data) {
    return '';
  }
  if (typeof data !== 'object') {
    return data;
  }
  const form = [];
  Object.keys(data || {}).forEach(key => {
    if (excludeUndefined && data[key] === undefined) {
      return;
    }
    form.push(`${key}=${data[key]}`);
  });

  return form.join('&');
}

export function appendParamsToQueryString(url, paramsString) {
  if (paramsString) {
    const mark = url.match(/\?/) ? '&' : '?';
    return `${url}${mark}${paramsString}`;
  }
  return url;
}

function addCookies(options) {
  if (!isClient && !isReactNative) {
    let cookieStr = '';
    forEach(reactCookie.loadAll(), (val, key) => {
      cookieStr += `${cookie.serialize(key, val)}; `;
    });
    options = {
      ...options,
      headers: {
        ...options.headers,
        Cookie: cookieStr,
      },
    };
  }
  return options;
}

const hashCode = s => {
  const l = s.length;
  let h = 0;
  let i = 0;
  if (l > 0) while (i < l) h = ((h << 5) - h + s.charCodeAt(i++)) | 0; // eslint-disable-line
  return h;
};

const cache = {};

const cleanCache = throttle(
  () => {
    const now = Date.now();
    forEach(cache, (val, key) => {
      if (val.expires < now) delete cache[key];
    });
  },
  60 * 1000,
  { trailing: false },
);

export function invalidateCache(urlOrFetchOptions) {
  const url =
    urlOrFetchOptions && urlOrFetchOptions.url
      ? appendParamsToQueryString(urlOrFetchOptions.url, paramify(urlOrFetchOptions.data || {}))
      : urlOrFetchOptions;
  if (url) {
    const hash = hashCode(url);
    delete cache[hash];
  }
}

export const getRandomId = (length = 10) => {
  const arr = [];

  while (arr.length * 10 <= length) {
    arr.push(
      Math.random()
        .toString(36)
        .substring(2),
    );
  }

  return arr.join('').substring(0, length);
};

const CSRF_HTTP_METHODS = ['patch', 'post', 'put', 'delete'];

function baseFetch({ url, state, options = {}, caching = {} }) {
  if (!url) {
    return Promise.reject(new Error('Fetching invalid URL'));
  }

  const hash = hashCode(url);
  const method = toLower(options.method);

  if (method && method !== 'get') {
    // non GET request. delete any caching for the corresponding GET
    delete cache[hash];
  } else if ((isClient || isReactNative) && caching.timeout) {
    // should we use the cached response?
    cleanCache();
    if (cache[hash] && cache[hash].ts + caching.timeout * 1000 > Date.now()) {
      return Promise.resolve(cache[hash].payload.clone());
    }
  }

  // should we send GH token in the header?
  if (url.startsWith(state.config.session.ghopper_url)) {
    options.headers = assign(options.headers, {
      'X-GH-Token': reactCookie.load('grass'),
    });
  }

  // should we add pkey/nkey/nocache to the request?
  if (url.startsWith(state.config.session.radish_url)) {
    const pkey = reactCookie.load('pkey');
    const nkey = reactCookie.load('nkey');

    if (pkey) url = appendParamsToQueryString(url, `pkey=${pkey}`);
    if (nkey) url = appendParamsToQueryString(url, `nkey=${nkey}`);

    if (!pkey && !nkey && reactCookie.load('euid')) {
      url = appendParamsToQueryString(url, `nocache=${getRandomId()}`);
    }

    const deviceId = state.device?.hi || reactCookie.load('hi');

    if (deviceId) url = appendParamsToQueryString(url, `hi=${deviceId}`);

    if (CSRF_HTTP_METHODS.includes(method)) {
      options.headers = assign(options.headers, {
        'X-CSRF-Token': getRandomId(36),
      });
    }
  }

  const { requestTimeoutMs = 0 } = options;
  const enforceTimeout = typeof requestTimeoutMs === 'number' && requestTimeoutMs > 0;
  const FORCE_LOGOUT_CODES = ['1', '2', '3'];

  return new Promise((resolve, reject) => {
    let requestTimeoutId;
    if (enforceTimeout) {
      requestTimeoutId = setTimeout(() => {
        requestTimeoutId = setTimeout(() => reject(new RequestError(RequestError.Type.timeout)), 0);
      }, requestTimeoutMs);
    }
    return fetch(url, {
      ...addCookies(options),
      credentials:
        options.credentials || url.startsWith(state.config.session.radish_url)
          ? 'include'
          : 'same-origin',
    })
      .then(payload => {
        if (enforceTimeout) {
          clearTimeout(requestTimeoutId);
        }

        const forceLogoutCode = payload.headers?.get('force-logout');

        // force logout user if neeeded
        if (
          !isReactNative &&
          url.startsWith(state.config.session.radish_url) && // is this a radish response?
          state.auth.signInType &&
          state.auth.signInType !== 'none' && // are we logged in (client side)?
          FORCE_LOGOUT_CODES.includes(forceLogoutCode) // do we need to force logout?
        ) {
          if (!forceLogout && typeof window !== 'undefined') {
            forceLogout = true; // we only want to redirect once, even if we got multiple responses with force-logout header
            if (process.env.IN_CHROMECAST_MODE) {
              // ignore for chromecast since redirecting to home page is not the case for chromecast
            } else {
              // web client - redirect to logout node route
              window.location.href = `${state.config.session.dany_base_url}/${
                state.config.session.dish_logout_action
              }`;
            }
          }

          return reject(payload);
        }

        // save cookies from server side rendering fetches
        if (!isClient && !isReactNative) {
          const resCookies = payload.headers.raw()['set-cookie'];
          if (resCookies) {
            const parsedCookies = resCookies.map(c => cookie.parse(c));
            parsedCookies.forEach(c => {
              const cookieName = Object.keys(c)[0];
              const cookieVal = c[cookieName];
              delete c[cookieName];
              if (c.expires) c.expires = new Date(c.expires);
              reactCookie.save(cookieName, cookieVal, c);
            });
          }
        }

        // cache the payload if needed
        if (
          (!options.method || options.method.toLowerCase() === 'get') &&
          caching.timeout &&
          (isClient || isReactNative)
        ) {
          const p = payload.clone();
          cache[hash] = {
            expires: Date.now() + caching.timeout * 1000,
            payload: p,
            ts: Date.now(),
          };
        }

        resolve(payload);
      })
      .catch(error => reject(error));
  });
}

export function get({ url, state, data, headers = {}, caching, requestTimeoutMs }) {
  return baseFetch({
    url: appendParamsToQueryString(url, paramify(data)),
    state,
    options: { headers, requestTimeoutMs },
    caching,
  });
}

export function getJson(options) {
  const { withResponseHeaders } = options;

  return get(options).then(async response => {
    if (!response.ok) {
      throw new RequestError(RequestError.Type.httpNonOk, response.status, response);
    }

    const json = response.status === 204 ? {} : await response.json();

    return withResponseHeaders
      ? {
          response: json,
          fetchResponse: response,
          headers: response.headers,
        }
      : json;
  });
}

export function post({ url, state, data, headers = {}, requestTimeoutMs }) {
  return baseFetch({
    url,
    state,
    options: {
      body: paramify(data),
      method: 'post',
      requestTimeoutMs,
      headers: {
        ...headers,
        'Content-Type': headers['Content-Type'] || 'application/x-www-form-urlencoded',
      },
    },
  });
}

export function postJson(options) {
  const { withResponseHeaders } = options;

  return post(options).then(async response => {
    if (!response.ok) {
      throw new RequestError(RequestError.Type.httpNonOk, response.status, response);
    }

    const json = response.status === 204 ? {} : await response.json();

    return withResponseHeaders
      ? {
          response: json,
          fetchResponse: response,
          headers: response.headers,
        }
      : json;
  });
}

export function del({ url, state, data, headers = {}, requestTimeoutMs }) {
  return baseFetch({
    url,
    state,
    options: {
      body: paramify(data),
      method: 'delete',
      requestTimeoutMs,
      headers: {
        ...headers,
        'Content-Type': headers['Content-Type'] || 'application/x-www-form-urlencoded',
      },
    },
  });
}

export function delJson(options) {
  return del(options).then(response => (response.status === 204 ? {} : response.json()));
}

export function put({ url, state, data, headers = {}, requestTimeoutMs }) {
  return baseFetch({
    url,
    state,
    options: {
      body: paramify(data),
      method: 'put',
      requestTimeoutMs,
      headers: {
        ...headers,
        'Content-Type': headers['Content-Type'] || 'application/x-www-form-urlencoded',
      },
    },
  });
}

export function putJson(options) {
  return put(options).then(response => response.json());
}
